/* eslint-disable react/jsx-props-no-spreading, sonarjs/cognitive-complexity */
import {
  useFormState,
  useStateObject,
  isObjectEmptyData,
} from 'holistic-resources';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from 'components/Button';
import Spinner from 'components/Spinner';
import DateField from 'components/formFields/DateField';
import TextField, { getInputStyle } from 'components/formFields/TextField';
import useInitializeApp from 'hooks/useInitializeApp';
import useWhiteLabelSettings from 'hooks/useWhiteLabelSettings';
import { ReactComponent as DefaultLogoIcon } from 'images/icons/inspiretec_blue_logo.svg';
import { setWhiteLabelSettings, toggleAppOverlay, toggleLoadingAppOverlay, setLoggedOutFromTimeout, resetUISlice } from 'reducers/ui/uiSlice';
import { resetCurrentUserSlice } from 'reducers/currentUser/currentUserSlice';
import { resetPaymentFormSlice } from 'reducers/paymentForm/paymentFormSlice';
import { resetSettingsSlice } from 'reducers/settingsSlice';
import { loginUser, getUIMessage } from 'services/ApiService';
import { API_BASE_URL } from 'shared/constants/appConfig';
import { KEY_LOGIN_REDIRECT_URL } from 'shared/constants/common';
import { ROUTES_SELF_SERVICE } from 'shared/constants/selfServiceRoutes';
import { fetchData, displayDate } from 'shared/helpers/common';

const getBackgroundStyle = (backgroundImage) => {
  const backgroundStyle = backgroundImage
    ? { backgroundImage: `url(${backgroundImage})` }
    : {};

  const backgroundClassNames = backgroundImage ? '' : 'login-bg-image';

  return { backgroundStyle, backgroundClassNames };
};

export default function Login() {
  const { value: formErrorMessages, updateValue: setFormErrorMessages } =
    useStateObject({
      dateOfBooking: '',
    });
  const { formValue: inputValues, formChangeHandler: changeInputHandler } =
    useFormState({
      reference: '',
      lastName: '',
      dateOfBooking: '',
    });

  const {
    whiteLabelSettings: {
      loginImage = '',
      loginLogo = '',
      copyrightMessage = '',
      email = '',
      phoneNumber = '',
    } = {},
  } = useWhiteLabelSettings();

  const { selectedBooking = {}, selectedQuote = {} } = useSelector((state) => state.ui);
  const componentConfig = useSelector((state) => state.ui.componentConfig || {});
  const [isInitializing, initializeApp] = useInitializeApp();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoginFailed, setIsLoginFailed] = useState(false);
  const [disabledLoginButton, setDisabledLoginButton] = useState(false);
  const [uiMessage, setUIMessage] = useState('');
  const [multiBookingRedirect, setMultiBookingRedirect] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setLoggedOutFromTimeout(false));
    dispatch(toggleLoadingAppOverlay(false));
  }, [dispatch]);

  const loadWhiteLabelSettings = useCallback(async () => {
    const { footer = {}, styling = {} } = await fetchData(
      `${API_BASE_URL}/api/self-service/config`,
      {},
    );

    dispatch(setWhiteLabelSettings({ ...footer, ...styling }));
  }, [dispatch]);

  useEffect(() => {
    loadWhiteLabelSettings();
  }, [loadWhiteLabelSettings]);

  const initializeRedirect = useCallback(() => {
    if (!Object.keys(componentConfig).length || (!Object.keys(selectedBooking).length && !Object.keys(selectedQuote).length)) {
      return;
    }

    let singleViewRedirect;
    if (!Object.keys(selectedBooking).length) {
      singleViewRedirect = `${ROUTES_SELF_SERVICE.QUOTES}/${selectedQuote.id || selectedQuote.reference}`;
    } else {
      singleViewRedirect = `${ROUTES_SELF_SERVICE.BOOKINGS}/${selectedBooking.id || selectedBooking.reference}`;
    }

    const redirect = componentConfig.multiBookingEnabled
      ? `${ROUTES_SELF_SERVICE.OVERVIEW}`
      : singleViewRedirect;
  
    setMultiBookingRedirect(redirect);
  }, [componentConfig, selectedBooking, selectedQuote]);

  useEffect(() => {
    if (!isInitializing) {
      const redirectUrl =
        localStorage.getItem(KEY_LOGIN_REDIRECT_URL) ||
        multiBookingRedirect;
      localStorage.setItem(KEY_LOGIN_REDIRECT_URL, '');
      navigate(redirectUrl);
    }
  }, [isInitializing, navigate, multiBookingRedirect]);

  useEffect(() => {
    initializeRedirect();
  }, [initializeRedirect]);

  const signInHandler = async () => {
    setDisabledLoginButton(true);
    try {
      dispatch(toggleAppOverlay(false));
      dispatch(resetUISlice());
      dispatch(resetCurrentUserSlice());
      dispatch(resetPaymentFormSlice());
      dispatch(resetSettingsSlice());

      const { lastName = '', reference = '', dateOfBooking = '' } = inputValues;
      const loginCredentials = {
        lastName,
        reference,
        dateOfBooking: dateOfBooking
          ? displayDate(inputValues.dateOfBooking, '', 'yyyy-MM-dd')
          : '',
      };
      await loginUser(loginCredentials);
      setIsLoginFailed(false);

      setIsLoggingIn(true);
      await initializeApp(loginCredentials, true);
      initializeRedirect();
      navigate(multiBookingRedirect);
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;
        if (status === 403 && data.trim() === 'group-disallowed') {
          const uIMessage = await getUIMessage(data.trim());
          setUIMessage(uIMessage.data);
        }
      }
      setIsLoginFailed(true);
      setDisabledLoginButton(false);
      setIsLoggingIn(false);
      console.error(error);
    }
  };

  const { backgroundStyle, backgroundClassNames } =
    getBackgroundStyle(loginImage);
  const textFieldStyle = `
    margin-bottom: 1.5rem !important;

    label {
      color: #000;
      font-size: 16px;
    }

    input {
      background: #FAFAFA;
      border: 1px solid #EEEEEE;
      border-radius: 5px;
      padding: 6px 12px;
      width: 100%;
      height: 50px;
      font-size: 15px;
    }

    input:focus {
      background: #FAFAFA;
    }
  `;

  const dateFieldStyles = `
  .text-sm {
    color: #000;
    font-size: 16px;
  } 
  
  input {
    height: 50px; 
    font-size: 15px;
  }

  .react-datepicker-wrapper {
    display: block;
  }`;

  return isLoggingIn ? (
    <Spinner className="h-screen" />
  ) : (
    <div
      style={backgroundStyle}
      className={`login-container ${backgroundClassNames} bg-cover`}
    >
      <div className="flex w-full">
        <div
          className="login-form container bg-white flex flex-col items-center justify-around !w-full"
          id="loginForm"
        >
          <div className="w-[75%]">
            <div className="flex">
              {loginLogo ? (
                <img src={loginLogo} className="max-h-14" alt="logo" />
              ) : (
                <DefaultLogoIcon className="h-11" />
              )}
            </div>
            <div className="form mt-[32px] md:mt-24">
              <div className="mb-5 text-[24px] lg:text-[32px] font-medium">
                Manage your booking
              </div>
              <TextField
                customStyles={textFieldStyle}
                labelCssClass="mb-[16px]"
                placeholder="Enter booking reference"
                label="Booking reference"
                name="reference"
                value={inputValues.reference}
                onChange={changeInputHandler}
              />
              <TextField
                customStyles={textFieldStyle}
                labelCssClass="mb-[16px]"
                placeholder="Enter Lead Bookers Last Name"
                label="Lead Bookers Last Name"
                name="lastName"
                value={inputValues.lastName}
                onChange={changeInputHandler}
              />
              <DateField
                name="dateOfBooking"
                placeholder="Enter date e.g. 01/02/21"
                timestampValue={inputValues?.dateOfBooking}
                onChange={changeInputHandler}
                label="Departure date"
                errorMessage={formErrorMessages?.dateOfBooking}
                setFormErrorMessages={setFormErrorMessages}
                inputClassName={getInputStyle(
                  formErrorMessages?.dateOfBooking,
                  true,
                )}
                customStyles={dateFieldStyles}
                labelClassName="text-gray-500 my-1 text-sm mb-[16px]"
              />
              {isLoginFailed && (
                <div className="text-error text-center mt-6 mb-2">
                  {uiMessage || 'Booking not found'}
                </div>
              )}
              <Button
                disabled={
                  !isObjectEmptyData(formErrorMessages) || disabledLoginButton
                }
                label="Sign in"
                onClick={signInHandler}
                className="w-full mt-12"
              />
            </div>
            <div className="mt-12 leading-[2] hidden">
              <div className="font-bold text-[14px]">
                Having problems signing in? Get in touch:
              </div>
              <div className="font-normal underline decoration-solid decoration-1">
                <a href={`mailto:${email}`}>{email}</a>
              </div>
              <div className="font-normal">{phoneNumber}</div>
            </div>
          </div>
          <div className="copyright text-center">{copyrightMessage}</div>
        </div>
      </div>
    </div>
  );
}
